<!--
  RARECARD 卡牌助手 · 官网接入组件

  依赖：本地运行 openrare-assistant 服务（默认 127.0.0.1:8090），
        vite.config.ts 已把 /assistant 代理过去，局域网访问走同一路径。

  配置写在 public/content/site.json 的 assistant 节点，改配置不用改代码。
  排查：地址栏加 ?assistant=debug 打开详细日志，?assistant=off 临时关掉助手。
-->
<script setup lang="ts">
import { onBeforeUnmount, onMounted, watch } from 'vue'
import { useColorTheme } from '../composables/useColorTheme'
import { usePageLoaded } from '../composables/usePageLoaded'
import type { SiteConfig } from '../types/site'

const props = defineProps<{
  config: SiteConfig
  locale: string
}>()

const { theme } = useColorTheme()
const pageLoaded = usePageLoaded()

const TAG = '[RARECARD 助手]'
const params = new URLSearchParams(typeof window === 'undefined' ? '' : window.location.search)
const DEBUG = params.get('assistant') === 'debug' || import.meta.env.DEV
const DISABLED = params.get('assistant') === 'off'

const log = (...a: unknown[]) => { if (DEBUG) console.info(TAG, ...a) }
const warn = (...a: unknown[]) => console.warn(TAG, ...a)

type WidgetApi = {
  init: (opt: Record<string, unknown>) => unknown
  destroy: () => void
  open: () => void
  close: () => void
  resetPosition?: () => void
  setTheme?: (t: string) => void
  setLocale?: (l: string) => void
}
const api = () => (window as unknown as { OpenRareAssistant?: WidgetApi }).OpenRareAssistant

let booted = false

function badge(text: string) {
  if (!import.meta.env.DEV) return
  const el = document.createElement('div')
  el.textContent = TAG + ' ' + text
  el.style.cssText =
    'position:fixed;right:12px;bottom:12px;z-index:2147483000;background:#b3261e;color:#fff;' +
    'font:12px/1.5 -apple-system,"PingFang SC",sans-serif;padding:8px 12px;border-radius:8px;max-width:260px'
  el.onclick = () => el.remove()
  document.body.appendChild(el)
}

function pickLocale() {
  const a = props.config?.assistant
  const locales = a?.locales?.length ? a.locales : ['zh']
  return locales.includes(props.locale) ? props.locale : 'zh'
}

function boot() {
  const a = props.config?.assistant
  const w = api()
  if (!a?.enabled || !w) return

  log('init', { locale: pickLocale(), theme: theme.value, endpoint: a.endpoint })
  w.init({
    endpoint: a.endpoint || '/assistant/api/chat',
    theme: theme.value,
    form: 'auto',
    locale: pickLocale(),
    offset: a.offset || { right: 24, bottom: 26 },
    // 品牌色跟随 site.json 的 theme，深色模式下也保持官网的黄
    accent: props.config.theme?.accent,
    onAccent: props.config.theme?.ink,
    quick: a.quick?.[pickLocale()] || a.quick?.zh || null,
    quickByLocale: a.quick || null,
    // 点击时再读配置：后台的运行时覆盖（loginUrl / handoffUrl）可能在 widget 启动之后才到
    onLogin: () => { const c = props.config; window.location.href = c.assistant?.loginUrl || c.links.heroCta },
    onHandoff: () => { const c = props.config; window.location.href = c.assistant?.handoffUrl || c.links.about },
  })
  booted = true
}

onMounted(() => {
  if (DISABLED) { log('已被 ?assistant=off 关闭'); return }

  const a = props.config?.assistant
  if (!a) { warn('site.json 里没有 assistant 节点，助手不会显示。若刚改过配置，请强制刷新（Ctrl+Shift+R）。'); return }
  if (!a.enabled) { log('site.json 里 assistant.enabled = false，跳过'); return }
  if (window.location.protocol === 'file:') { log('离线单文件版，跳过'); return }

  if (api()) { boot(); return }

  // 助手脚本等页面 load 之后再加载：动态插入的脚本也会拖住 load，
  // 而微信要等 load 后才放行首屏视频自动播放（见 usePageLoaded）。
  if (pageLoaded.value) loadWidget(a)
  else {
    const stop = watch(pageLoaded, (ready) => {
      if (!ready) return
      stop()
      loadWidget(a)
    })
  }
})

function loadWidget(a: NonNullable<SiteConfig['assistant']>) {
  const src = a.script || '/assistant/widget.js'
  if (document.getElementById('openrare-assistant-loader')) return

  log('加载 widget：' + src)
  const s = document.createElement('script')
  s.id = 'openrare-assistant-loader'
  s.src = src
  s.async = true
  s.onload = () => { log('widget 已加载'); boot() }
  s.onerror = () => {
    s.remove()
    warn(`widget 加载失败：${src}。请确认助手服务已启动（openrare-assistant 目录下 npm start），` +
      `并检查 ${new URL('/assistant/api/health', window.location.origin).href} 是否返回 JSON。`)
    badge('widget 加载失败，看控制台')
  }
  document.head.appendChild(s)
}

/*
 * 主题和语言用轻量接口更新，不重建实例。
 * 早期版本这里是重新 init 一次，每切一次就多泄漏一套全局监听和一个 WebGL 上下文，
 * 切到十几次以后辉光会直接失效（浏览器对 WebGL 上下文数量有上限）。
 */
watch(() => theme.value, t => { if (booted) api()?.setTheme?.(t) })
watch(() => props.locale, () => { if (booted) api()?.setLocale?.(pickLocale()) })
// 后台把助手关掉时（运行时覆盖 enabled=false），已启动的实例直接销毁
watch(() => props.config?.assistant?.enabled, (enabled) => {
  if (booted && enabled === false) { api()?.destroy(); booted = false; log('已按后台配置关闭') }
})

onBeforeUnmount(() => api()?.destroy())
</script>

<template><span class="assistant-mount" aria-hidden="true"></span></template>

<style scoped>
.assistant-mount { display: none; }
</style>
